import { cs } from "../../utils/currency";
import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  RefreshCw,
  LayoutGrid,
  List,
  GripVertical,
  Eye,
  ChefHat,
} from "lucide-react";
import api, { getCodes, getCategories } from "../../services/api";
import { toast } from "react-hot-toast";
import AddMenuDialog from "../../components/AddMenuDailog";
import { getTokenInfo } from "../../utils/auth";

interface CategoryCode {
  _id: string;
  code: string;
  name: string;
  taxable: boolean;
}

interface Category {
  _id: string;
  name: string;
  imageUrl?: string;
  categoryCode?: CategoryCode;
  taxable?: boolean;
}

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  discount?: number;
  category: string;
  categoryId: string;
  categoryCode?: string;
  imageUrl: string;
  description?: string;
  tags?: string[];
  customize?: any[];
  foodType: "veg" | "non-veg" | "both";
  fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[];
  kitchenName?: string;
  prepTime?: number;
}

// GET /menu may return categoryId populated or as a plain id
const getCategoryId = (item: any): string =>
  typeof item.categoryId === "object" && item.categoryId
    ? item.categoryId._id
    : item.categoryId || "";

const MenuManagement: React.FC = () => {
  // Location admins can only view items and toggle per-location settings
  const isMasterAdmin = getTokenInfo()?.role === "master_admin";

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [codes, setCodes] = useState<CategoryCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Drag-to-reorder (list view, single category, no search)
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  const [dragOverItem, setDragOverItem] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  // Add/edit dialog
  const [showAddMenuDialog, setShowAddMenuDialog] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState<MenuItem | null>(null);
  const [dialogCategory, setDialogCategory] = useState<Category | null>(null);

  // Category picker shown before adding an item when no category is filtered
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [pickerCategoryId, setPickerCategoryId] = useState("");

  // Items come from the admin endpoints (GET /menu is the kiosk endpoint and
  // drops items that aren't enabled for self-ordering). "All" is one request;
  // a single category uses the per-category endpoint so the order matches
  // what reordering expects.
  const fetchMenuItems = async (categoryId = selectedCategoryId) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(
        categoryId ? `/menu/category/${categoryId}` : "/menu/all",
      );
      const items = categoryId ? res.data?.data?.menuItems : res.data?.data;
      setMenuItems(Array.isArray(items) ? items : []);
    } catch (err: any) {
      setError("Failed to load menu items.");
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategories()
      .then((res) => setCategories(res.data.data || []))
      .catch(() => {
        setCategories([]);
        setError("Failed to load categories.");
      });
    getCodes()
      .then((res) => setCodes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCodes([]));
  }, []);

  useEffect(() => {
    fetchMenuItems(selectedCategoryId);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId]);

  // In "All" view, group items by category order (as the per-category
  // listing did), each item once under its first matching category
  const orderedItems = useMemo(() => {
    if (selectedCategoryId || categories.length === 0) return menuItems;
    const rank = new Map(categories.map((c, i) => [c._id, i]));
    const firstRank = (item: any) => {
      const ids = [
        getCategoryId(item),
        ...(item.additionalCategoryIds || []).map((c: any) =>
          typeof c === "object" ? c._id : c,
        ),
      ];
      return Math.min(...ids.map((id) => rank.get(id) ?? Infinity));
    };
    return menuItems
      .map((item, i) => ({ item, i, r: firstRank(item) }))
      .sort((a, b) => a.r - b.r || a.i - b.i)
      .map((x) => x.item);
  }, [menuItems, categories, selectedCategoryId]);

  const categoryById = useMemo(() => {
    const map: Record<string, Category> = {};
    categories.forEach((c) => (map[c._id] = c));
    return map;
  }, [categories]);

  const filteredItems = orderedItems.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      item.name.toLowerCase().includes(term) ||
      (item.category || "").toLowerCase().includes(term) ||
      (item.tags || []).some((t) => t.toLowerCase().includes(term))
    );
  });

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems =
    viewMode === "grid"
      ? filteredItems.slice(startIndex, startIndex + itemsPerPage)
      : filteredItems;

  const canReorder =
    isMasterAdmin && viewMode === "list" && !!selectedCategoryId && !searchTerm;

  const openItem = (item: MenuItem) => {
    const categoryId = getCategoryId(item);
    setDialogCategory(
      categoryById[categoryId] || { _id: categoryId, name: item.category },
    );
    setEditingMenuItem({ ...item, categoryId });
    setShowAddMenuDialog(true);
  };

  const startAdd = () => {
    if (selectedCategoryId && categoryById[selectedCategoryId]) {
      setDialogCategory(categoryById[selectedCategoryId]);
      setEditingMenuItem(null);
      setShowAddMenuDialog(true);
      return;
    }
    setPickerCategoryId("");
    setShowCategoryPicker(true);
  };

  const confirmCategoryPick = () => {
    const category = categoryById[pickerCategoryId];
    if (!category) {
      toast.error("Please select a category");
      return;
    }
    setShowCategoryPicker(false);
    setDialogCategory(category);
    setEditingMenuItem(null);
    setShowAddMenuDialog(true);
  };

  const handleDelete = async (itemId: string) => {
    if (!window.confirm("Are you sure you want to delete this menu item?"))
      return;
    try {
      const response = await api.delete(`/menu/${itemId}`);
      if (response.data.success) {
        toast.success("Menu item deleted successfully!");
        fetchMenuItems();
      } else {
        toast.error(response.data.message || "Failed to delete menu item");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to delete menu item");
    }
  };

  const reorderMenuItems = async (fromIndex: number, toIndex: number) => {
    setIsReordering(true);
    try {
      const response = await api.put("/menu/reorder", {
        categoryId: selectedCategoryId,
        fromIndex,
        toIndex,
      });
      if (response.data.success) {
        setMenuItems(response.data.data.menuItems);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || "Failed to reorder menu items");
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to reorder menu items");
      fetchMenuItems();
    } finally {
      setIsReordering(false);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedItem !== null && draggedItem !== dropIndex) {
      reorderMenuItems(draggedItem, dropIndex);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const getFoodTypeBadge = (foodType: string) => {
    switch (foodType) {
      case "veg":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Veg
          </span>
        );
      case "non-veg":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Non-Veg
          </span>
        );
      case "both":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Both
          </span>
        );
      default:
        return null;
    }
  };

  const renderPrice = (item: MenuItem) =>
    item.discount ? (
      <>
        <span className="text-lg font-bold text-green-600">
          ${(item.price * (1 - item.discount / 100)).toFixed(0)}
        </span>
        <span className="text-sm text-gray-500 line-through">{cs()}{item.price}</span>
      </>
    ) : (
      <span className="text-lg font-bold text-gray-900">{cs()}{item.price}</span>
    );

  const renderActions = (item: MenuItem, compact = false) => (
    <div className={`flex items-center gap-1 ${compact ? "" : "w-full sm:w-auto"}`}>
      <button
        onClick={() => openItem(item)}
        className="flex-1 sm:flex-none p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
        title={isMasterAdmin ? "Edit Menu Item" : "View Menu Item"}
      >
        {isMasterAdmin ? <Edit size={16} /> : <Eye size={16} />}
      </button>
      {isMasterAdmin && (
        <button
          onClick={() => handleDelete(item._id)}
          className="flex-1 sm:flex-none p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
          title="Delete Menu Item"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col h-screen overflow-y-auto no-scrollbar">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-row justify-between items-center sm:gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Menu Items</h1>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="Grid View"
              >
                <LayoutGrid size={18} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md transition-all ${
                  viewMode === "list"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                title="List View"
              >
                <List size={18} />
              </button>
            </div>

            {isMasterAdmin && (
              <button
                onClick={startAdd}
                className="px-2.5 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base whitespace-nowrap"
              >
                <Plus size={16} />
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Add Menu Item</span>
              </button>
            )}
          </div>
        </div>

        {/* ================= SEARCH & FILTER ================= */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end mb-2">
          <div className="flex-1">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Search Menu Items
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by name, category or tag..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1 sm:w-56">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Category
              </label>
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full p-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => fetchMenuItems()}
              className="w-10 h-10 flex-shrink-0 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Menu Items"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {isMasterAdmin && viewMode === "list" && !canReorder && (
          <p className="text-xs text-gray-500">
            Select a single category (and clear the search) to drag items into
            a new order.
          </p>
        )}
        {isReordering && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-3 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-blue-700 text-sm">Updating item order...</span>
          </div>
        )}
      </div>

      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : paginatedItems.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <ChefHat size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium">No menu items found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : isMasterAdmin
                    ? "Add your first menu item to get started"
                    : "No menu items have been added yet"}
              </p>
            </div>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedItems.map((item) => (
              <div
                key={item._id}
                className="bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <div
                  className="aspect-video bg-gray-200 relative overflow-hidden cursor-pointer"
                  onClick={() => openItem(item)}
                >
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2">
                    {getFoodTypeBadge(item.foodType)}
                  </div>
                  {item.discount ? (
                    <div className="absolute top-2 left-2">
                      <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        {item.discount}% OFF
                      </span>
                    </div>
                  ) : null}
                </div>

                <div className="p-4">
                  <p className="text-xs font-medium text-blue-600 mb-1">
                    {item.category}
                  </p>
                  <h4 className="text-xl font-bold text-gray-800">{item.name}</h4>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                    {item.description}
                  </p>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                      {item.tags.length > 3 && (
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                          +{item.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">{renderPrice(item)}</div>
                    {renderActions(item, true)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="divide-y divide-gray-200">
              {paginatedItems.map((item, index) => (
                <div
                  key={item._id}
                  draggable={canReorder && !isReordering}
                  onDragStart={(e) => {
                    setDraggedItem(index);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverItem(index);
                  }}
                  onDragLeave={() => setDragOverItem(null)}
                  onDrop={(e) => handleDrop(e, index)}
                  className={`flex flex-col gap-3 p-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:gap-4 sm:p-4 ${
                    draggedItem === index ? "opacity-50" : ""
                  } ${dragOverItem === index ? "bg-blue-50 border-blue-200" : ""}`}
                >
                  {canReorder && (
                    <div className="hidden flex-shrink-0 cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing sm:block">
                      <GripVertical size={20} />
                    </div>
                  )}

                  <div className="h-20 w-full flex-shrink-0 overflow-hidden rounded-lg bg-gray-200 sm:w-20">
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-blue-600">
                          {item.category}
                        </p>
                        <h4 className="text-xl font-bold text-gray-800">
                          {item.name}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {item.description}
                        </p>
                      </div>
                      <div className="text-left sm:ml-4 sm:text-right">
                        <div className="flex items-center gap-2 mb-2">
                          {getFoodTypeBadge(item.foodType)}
                          {item.discount ? (
                            <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                              {item.discount}% OFF
                            </span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                          {renderPrice(item)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ml-0 flex-shrink-0 sm:ml-4">
                    {renderActions(item)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {viewMode === "grid" && filteredItems.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center border-t">
          <div className="flex flex-wrap justify-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded transition ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= CATEGORY PICKER ================= */}
      {showCategoryPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowCategoryPicker(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="text-lg font-bold text-gray-800">
                Choose a category
              </h2>
              <button
                type="button"
                onClick={() => setShowCategoryPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5">
              {categories.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Create a category on the Categories page before adding menu
                  items.
                </p>
              ) : (
                <select
                  value={pickerCategoryId}
                  onChange={(e) => setPickerCategoryId(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a category</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t bg-gray-50 px-5 py-4 rounded-b-lg">
              <button
                type="button"
                onClick={() => setShowCategoryPicker(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmCategoryPick}
                disabled={!pickerCategoryId}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= ADD/EDIT MENU ITEM DIALOG ================= */}
      <AddMenuDialog
        isOpen={showAddMenuDialog}
        onClose={() => {
          setShowAddMenuDialog(false);
          setEditingMenuItem(null);
        }}
        categoryId={dialogCategory?._id || ""}
        categoryName={dialogCategory?.name || ""}
        categoryCodeId={dialogCategory?.categoryCode?._id || ""}
        categoryCodes={codes}
        categories={categories}
        onMenuItemAdded={() => fetchMenuItems()}
        editingItem={editingMenuItem}
      />
    </div>
  );
};

export default MenuManagement;
